import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSearch, FaFilter, FaDownload, FaEnvelope,
  FaCheckCircle, FaTimesCircle, FaEye, FaTimes,
  FaCalendarAlt, FaClock, FaToggleOn, FaToggleOff,
  FaTrash, FaChartLine
} from 'react-icons/fa';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Navbar  from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';

import { API_URL } from '../config/api';
const SB_OPEN    = 252;
const SB_CLOSED  =  74;
const SB_GAP     =  12;
const NB_HEIGHT  =  68;
const NB_TOP     =  12;
const PER_PAGE   =  10;

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Sk = ({ h = 40, r = 8 }) => (
  <div style={{
    height: h, borderRadius: r,
    background: 'linear-gradient(90deg,rgba(128,128,128,0.08) 25%,rgba(128,128,128,0.15) 50%,rgba(128,128,128,0.08) 75%)',
    backgroundSize: '200% 100%', animation: 'nl-shimmer 1.4s infinite',
  }} />
);

const Newsletter = () => {
  const [sidebarOpen, setSidebarOpen]               = useState(true);
  const [searchQuery, setSearchQuery]               = useState('');
  const [filterStatus, setFilterStatus]             = useState('all');
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showModal, setShowModal]                   = useState(false);
  const [currentPage, setCurrentPage]               = useState(1);
  const { theme }                                   = useTheme();
  const isDark                                      = theme === 'dark';

  // ── État données ─────────────────────────────────────────────────────────────
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats]             = useState({ total:0, active:0, inactive:0, growth:'+0%' });
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [loading, setLoading]         = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // id en cours d'action

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── Fetch stats ───────────────────────────────────────────────────────────────
  const fetchStats = useCallback(() => {
    setLoadingStats(true);
    axios.get(`${API_URL}/admin/newsletter/stats`, { headers })
      .then(({ data }) => setStats(data))
      .finally(() => setLoadingStats(false));
  }, []);

  // ── Fetch liste ───────────────────────────────────────────────────────────────
  const fetchSubscribers = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/admin/newsletter`, {
      headers,
      params: {
        page:     currentPage,
        per_page: PER_PAGE,
        status:   filterStatus !== 'all' ? filterStatus : undefined,
        search:   searchQuery || undefined,
      },
    })
    .then(({ data }) => {
      setSubscribers(data.data);
      setTotalPages(data.last_page);
      setTotalItems(data.total);
    })
    .finally(() => setLoading(false));
  }, [currentPage, filterStatus, searchQuery]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  // Reset page quand filtre/recherche change
  useEffect(() => { setCurrentPage(1); }, [filterStatus, searchQuery]);

  // ── Toggle statut ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (id) => {
    setActionLoading(id + '-toggle');
    try {
      const { data } = await axios.patch(`${API_URL}/admin/newsletter/${id}/toggle`, {}, { headers });
      setSubscribers(prev => prev.map(s => s.id === id ? { ...s, status: data.status } : s));
      // Mettre à jour le modal si ouvert
      if (selectedSubscriber?.id === id) setSelectedSubscriber(prev => ({ ...prev, status: data.status }));
      fetchStats();
    } finally {
      setActionLoading(null);
    }
  };

  // ── Supprimer ──────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    setActionLoading(id + '-delete');
    try {
      await axios.delete(`${API_URL}/admin/newsletter/${id}`, { headers });
      setShowModal(false);
      setSelectedSubscriber(null);
      fetchSubscribers();
      fetchStats();
    } finally {
      setActionLoading(null);
    }
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const headers_ = ['#', 'Email', "Date d'inscription", 'Source', 'Statut'];
    const rows     = subscribers.map((s, i) => [
      i + 1, s.email, formatDate(s.created_at), s.source || '-',
      s.status === 'active' ? 'Actif' : 'Inactif',
    ]);
    const csv  = [headers_, ...rows].map(r => r.join(',')).join('\n');
    const link = document.createElement('a');
    link.href  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `newsletter_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) : '—';
  const formatTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }) : '';

  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;

  const P = isDark ? {
    page:'#070a12', panel:'#0c1020', panelBorder:'rgba(255,255,255,0.07)',
    card:'rgba(255,255,255,0.04)', cardBorder:'rgba(255,255,255,0.08)',
    surface:'rgba(255,255,255,0.05)', surfBorder:'rgba(255,255,255,0.09)',
    text:'#c8d4e0', textDim:'#4e6070', title:'#eef2f7',
    inputBg:'rgba(255,255,255,0.06)', inputBorder:'rgba(255,255,255,0.11)',
    rowHover:'rgba(253,126,20,0.035)', tableHead:'rgba(255,255,255,0.025)',
    tableLine:'rgba(255,255,255,0.055)', numBg:'rgba(255,255,255,0.08)',
    numColor:'#6a8090', shadow:'0 2px 20px rgba(0,0,0,0.5)',
    modalBg:'#0d1220', modalBorder:'rgba(255,255,255,0.1)',
    detailBg:'rgba(255,255,255,0.04)', detailBorder:'rgba(255,255,255,0.08)',
    filterBorder:'rgba(255,255,255,0.1)', heroBg:'rgba(255,255,255,0.03)', heroBorder:'rgba(255,255,255,0.07)',
  } : {
    page:'#eef1f7', panel:'#ffffff', panelBorder:'rgba(0,0,0,0.07)',
    card:'#ffffff', cardBorder:'rgba(0,0,0,0.07)',
    surface:'#f7f9fb', surfBorder:'rgba(0,0,0,0.07)',
    text:'#4d6070', textDim:'#9ab0c0', title:'#1a2535',
    inputBg:'#ffffff', inputBorder:'rgba(0,0,0,0.1)',
    rowHover:'rgba(253,126,20,0.03)', tableHead:'#f9fafb',
    tableLine:'rgba(0,0,0,0.055)', numBg:'rgba(0,0,0,0.05)',
    numColor:'#8fa0b0', shadow:'0 2px 16px rgba(0,0,0,0.07)',
    modalBg:'#ffffff', modalBorder:'rgba(0,0,0,0.08)',
    detailBg:'#f9fafb', detailBorder:'rgba(0,0,0,0.07)',
    filterBorder:'rgba(0,0,0,0.09)', heroBg:'#ffffff', heroBorder:'rgba(0,0,0,0.07)',
  };

  const startIndex = (currentPage - 1) * PER_PAGE;

  return (
    <div style={{ background:P.page, minHeight:'100vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:'Plus Jakarta Sans',sans-serif}
        @keyframes nl-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}
        @keyframes nl-fo{from{opacity:0}to{opacity:1}}
        @keyframes nl-up{from{opacity:0;transform:translateY(24px) scale(0.97)}to{opacity:1;transform:none}}
        @keyframes nl-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}

        .nl-main  {transition:margin-left 0.28s cubic-bezier(0.4,0,0.2,1);min-height:100vh}
        .nl-body  {padding:22px 22px 40px}

        .nl-hero  {border-radius:16px;padding:28px 32px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;gap:24px;border:1px solid;position:relative;overflow:hidden}
        .nl-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,#fd7e14 0%,#20c997 50%,#198754 100%);border-radius:16px 16px 0 0}
        .nl-hero-left{display:flex;align-items:center;gap:18px}
        .nl-hero-icon{width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:20px;color:#fd7e14;flex-shrink:0;border:1px solid rgba(253,126,20,0.25);background:rgba(253,126,20,0.08)}
        .nl-hero-text{display:flex;flex-direction:column;gap:3px}
        .nl-hero-eyebrow{font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#fd7e14;opacity:0.8}
        .nl-hero-title{font-size:22px;font-weight:700;letter-spacing:-0.3px;line-height:1.2}
        .nl-hero-sub{font-size:14px;font-weight:400;line-height:1.5;max-width:480px;opacity:0.7}
        .nl-hero-right{display:flex;align-items:center;gap:20px;flex-shrink:0}
        .nl-hero-kpis{display:flex;gap:24px;padding-right:24px;border-right:1px solid}
        .nl-hero-kpi{display:flex;flex-direction:column;gap:2px;align-items:flex-end}
        .nl-hero-kpi-num{font-size:22px;font-weight:700;line-height:1}
        .nl-hero-kpi-label{font-size:8.5px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;opacity:0.45}
        .nl-export-btn{display:flex;align-items:center;gap:8px;padding:11px 22px;background:transparent;color:#fd7e14;border:1.5px solid rgba(253,126,20,0.4);border-radius:11px;font-size:14.5px;font-weight:600;cursor:pointer;transition:all 0.2s;white-space:nowrap}
        .nl-export-btn:hover{background:rgba(253,126,20,0.08);border-color:#fd7e14}

        .nl-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
        .nl-stat{border-radius:14px;border:1px solid;padding:22px 20px;display:flex;align-items:center;gap:16px;transition:transform 0.2s,box-shadow 0.2s;cursor:default;position:relative;overflow:hidden}
        .nl-stat:hover{transform:translateY(-3px)}
        .nl-stat-icon{width:48px;height:48px;border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:19px;border:1px solid;flex-shrink:0}
        .nl-stat-content{display:flex;flex-direction:column;gap:4px;flex:1;min-width:0}
        .nl-stat-num{font-size:28px;font-weight:700;letter-spacing:-1px;line-height:1}
        .nl-stat-label{font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .nl-stat-tag{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;padding:3px 8px;border-radius:5px;border:1px solid;align-self:flex-start;margin-top:2px}
        .nl-stat-deco{position:absolute;right:-20px;top:50%;transform:translateY(-50%);width:70px;height:70px;border-radius:50%;opacity:0.07}

        .nl-panel{border-radius:16px;border:1px solid;overflow:hidden}
        .nl-panel-head{display:flex;align-items:center;justify-content:space-between;padding:24px 28px;border-bottom:1px solid;position:relative;overflow:hidden}
        .nl-panel-head::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,#fd7e14 0%,#20c997 100%)}
        .nl-panel-title{font-size:18px;font-weight:700;letter-spacing:-0.2px;margin-bottom:3px}
        .nl-panel-sub{font-size:13.5px;font-weight:400}

        .nl-filters{display:flex;align-items:center;gap:12px;padding:16px 28px;border-bottom:1px solid;flex-wrap:wrap}
        .nl-search-wrap{flex:1;min-width:260px;position:relative;display:flex;align-items:center}
        .nl-search-ico{position:absolute;left:15px;font-size:14px;pointer-events:none;z-index:1}
        .nl-search-input{width:100%;height:44px;padding:0 16px 0 44px;border:1.5px solid;border-radius:11px;font-size:14.5px;font-weight:400;outline:none;transition:border-color 0.2s,box-shadow 0.2s}
        .nl-search-input::placeholder{color:#9ab0c0}
        .nl-search-input:focus{border-color:#fd7e14 !important;box-shadow:0 0 0 3px rgba(253,126,20,0.1)}
        .nl-filter-pills{display:flex;gap:6px}
        .nl-pill{display:flex;align-items:center;gap:7px;padding:0 16px;height:44px;border-radius:11px;border:1.5px solid;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;white-space:nowrap;background:transparent}
        .nl-pill--on      {background:#fd7e14 !important;border-color:#fd7e14 !important;color:#fff !important;box-shadow:0 3px 12px rgba(253,126,20,0.25)}
        .nl-pill--on-green{background:#198754 !important;border-color:#198754 !important;color:#fff !important;box-shadow:0 3px 12px rgba(25,135,84,0.25)}
        .nl-pill--on-red  {background:#dc3545 !important;border-color:#dc3545 !important;color:#fff !important;box-shadow:0 3px 12px rgba(220,53,69,0.25)}
        .nl-result-pill{margin-left:auto;padding:7px 14px;border-radius:20px;font-size:13.5px;font-weight:600;color:#fd7e14;background:rgba(253,126,20,0.08);border:1px solid rgba(253,126,20,0.18);white-space:nowrap}

        .nl-table-wrap{overflow-x:auto}
        .nl-table{width:100%;border-collapse:collapse;min-width:860px}
        .nl-table thead tr{border-bottom:2px solid}
        .nl-table th{padding:14px 22px;text-align:left;font-size:9.5px;font-weight:700;letter-spacing:1.4px;text-transform:uppercase;white-space:nowrap}
        .nl-table tbody tr{border-bottom:1px solid;cursor:pointer;transition:background 0.14s}
        .nl-table tbody tr:last-child{border-bottom:none}
        .nl-table td{padding:15px 22px;vertical-align:middle}
        .nl-num{display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:9px;font-size:11px;font-weight:700}
        .nl-email-cell{display:flex;align-items:center;gap:12px}
        .nl-email-av{width:40px;height:40px;border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;flex-shrink:0;background:rgba(253,126,20,0.08);color:#fd7e14;border:1px solid rgba(253,126,20,0.18)}
        .nl-email-val{font-size:14.5px;font-weight:500}
        .nl-date{display:flex;flex-direction:column;gap:2px}
        .nl-date-main{font-size:14.5px;font-weight:500}
        .nl-date-sub{font-size:10.5px;font-weight:400}
        .nl-src{display:inline-block;padding:5px 12px;border-radius:7px;font-size:13.5px;font-weight:500;border:1px solid}
        .nl-status{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:30px;font-size:13.5px;font-weight:600;border:1.5px solid;white-space:nowrap}
        .nl-status.active  {color:#198754;background:rgba(25,135,84,0.08);border-color:rgba(25,135,84,0.2)}
        .nl-status.inactive{color:#dc3545;background:rgba(220,53,69,0.08);border-color:rgba(220,53,69,0.2)}
        .nl-acts{display:flex;gap:6px;justify-content:center}
        .nl-act{width:36px;height:36px;border-radius:9px;border:1.5px solid;background:transparent;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;transition:all 0.18s;padding:0}
        .nl-act:disabled{opacity:0.4;cursor:not-allowed}
        .nl-act.view:hover{background:rgba(253,126,20,0.08);border-color:#fd7e14;color:#fd7e14}
        .nl-act.tog:hover {background:rgba(25,135,84,0.08);border-color:#198754;color:#198754}
        .nl-act.del:hover {background:rgba(220,53,69,0.08);border-color:#dc3545;color:#dc3545}

        .nl-pager{display:flex;align-items:center;gap:6px;padding:16px 24px;border-top:1px solid;flex-wrap:wrap}
        .nl-pg{min-width:40px;height:40px;border-radius:10px;border:1.5px solid;background:transparent;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0 12px;transition:all 0.18s;white-space:nowrap}
        .nl-pg:hover:not(:disabled){border-color:#fd7e14;color:#fd7e14}
        .nl-pg.on{background:#fd7e14 !important;color:#fff !important;border-color:#fd7e14 !important}
        .nl-pg:disabled{opacity:0.28;cursor:not-allowed}
        .nl-pg-nums{display:flex;gap:5px}
        .nl-pg-info{font-size:13.5px;font-weight:500;margin-left:auto}

        .nl-empty{display:flex;flex-direction:column;align-items:center;gap:14px;padding:72px 20px;text-align:center}
        .nl-empty-ico{width:72px;height:72px;border-radius:20px;display:flex;align-items:center;justify-content:center;font-size:28px;background:rgba(253,126,20,0.07);color:#fd7e14;border:1px solid rgba(253,126,20,0.14)}

        .nl-overlay{position:fixed;inset:0;background:rgba(4,7,16,0.65);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;z-index:3000;padding:20px;animation:nl-fo 0.2s ease}
        .nl-modal{width:100%;max-width:540px;max-height:90vh;overflow-y:auto;border-radius:20px;border:1px solid;animation:nl-up 0.26s cubic-bezier(0.34,1.56,0.64,1);scrollbar-width:none}
        .nl-modal::-webkit-scrollbar{display:none}
        .nl-modal-head{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid;gap:14px;position:relative;overflow:hidden}
        .nl-modal-head::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,#fd7e14 0%,#dc3545 100%)}
        .nl-modal-head-l{display:flex;align-items:center;gap:14px}
        .nl-modal-av{width:48px;height:48px;border-radius:13px;background:rgba(253,126,20,0.1);color:#fd7e14;border:1.5px solid rgba(253,126,20,0.25);font-size:20px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .nl-modal-h{font-size:17px;font-weight:700;margin-bottom:2px}
        .nl-modal-s{font-size:13px;font-weight:400}
        .nl-modal-x{width:38px;height:38px;border-radius:10px;border:1.5px solid;background:transparent;display:flex;align-items:center;justify-content:center;font-size:15px;cursor:pointer;transition:all 0.18s;flex-shrink:0;padding:0}
        .nl-modal-x:hover{background:rgba(220,53,69,0.08);border-color:#dc3545;color:#dc3545}
        .nl-modal-body{padding:24px}
        .nl-detail-sect-title{font-size:9.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#fd7e14;margin-bottom:14px;display:flex;align-items:center;gap:10px}
        .nl-detail-sect-title::after{content:'';flex:1;height:1px;background:rgba(253,126,20,0.18)}
        .nl-detail-grid{display:grid;gap:10px}
        .nl-detail-item{display:flex;flex-direction:column;gap:5px;padding:13px 16px;border-radius:12px;border:1px solid}
        .nl-detail-lbl{font-size:9px;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;color:#8fa3b5}
        .nl-detail-val{font-size:15px;font-weight:500;display:flex;align-items:center}
        .nl-modal-foot{display:flex;gap:10px;padding:18px 24px;border-top:1px solid}
        .nl-mbt{flex:1;display:flex;align-items:center;justify-content:center;gap:9px;padding:13px;border-radius:12px;font-size:14.5px;font-weight:600;cursor:pointer;transition:all 0.2s;border:1.5px solid}
        .nl-mbt:disabled{opacity:0.5;cursor:not-allowed}
        .nl-mbt.sec{background:#1a2535;color:#fff;border-color:#1a2535}
        .nl-mbt.sec:hover:not(:disabled){opacity:0.85}
        .nl-mbt.dng{background:transparent;color:#dc3545;border-color:rgba(220,53,69,0.4)}
        .nl-mbt.dng:hover:not(:disabled){background:#dc3545;color:#fff;border-color:#dc3545}
        .nl-spinner{animation:nl-spin 0.8s linear infinite}

        @media(max-width:1300px){.nl-stats{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:1024px){.nl-main{margin-left:0 !important}}
        @media(max-width:768px){.nl-hero{flex-direction:column;align-items:flex-start;padding:20px}.nl-hero-kpis{display:none}.nl-stats{grid-template-columns:1fr 1fr}.nl-body{padding:14px 14px 32px}.nl-filters{flex-direction:column;align-items:stretch}.nl-search-wrap{min-width:100%}.nl-result-pill{margin-left:0}.nl-filter-pills{flex-wrap:wrap}}
        @media(max-width:480px){.nl-stats{grid-template-columns:1fr}}
      `}</style>

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar  sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Newsletter" />

      <main className="nl-main" style={{ marginLeft:mainML, paddingTop:mainPT }}>
        <div className="nl-body">

          {/* ── HERO ── */}
          <div className="nl-hero" style={{ background:P.heroBg, borderColor:P.heroBorder }}>
            <div className="nl-hero-left">
              <div className="nl-hero-icon"><FaEnvelope /></div>
              <div className="nl-hero-text">
                <span className="nl-hero-eyebrow">Gestion</span>
                <h1 className="nl-hero-title" style={{ color:P.title }}>Newsletter</h1>
                <p className="nl-hero-sub" style={{ color:P.text }}>
                  Gérez vos abonnés, suivez vos statistiques et exportez vos données en toute simplicité
                </p>
              </div>
            </div>
            <div className="nl-hero-right">
              <div className="nl-hero-kpis" style={{ borderRightColor:P.panelBorder }}>
                <div className="nl-hero-kpi">
                  <span className="nl-hero-kpi-num" style={{ color:P.title }}>{loadingStats ? '…' : stats.total}</span>
                  <span className="nl-hero-kpi-label" style={{ color:P.textDim }}>Total</span>
                </div>
                <div className="nl-hero-kpi">
                  <span className="nl-hero-kpi-num" style={{ color:'#20c997' }}>{loadingStats ? '…' : stats.active}</span>
                  <span className="nl-hero-kpi-label" style={{ color:P.textDim }}>Actifs</span>
                </div>
                <div className="nl-hero-kpi">
                  <span className="nl-hero-kpi-num" style={{ color:'#fd7e14' }}>{loadingStats ? '…' : stats.growth}</span>
                  <span className="nl-hero-kpi-label" style={{ color:P.textDim }}>Croissance</span>
                </div>
              </div>
              <button className="nl-export-btn" onClick={handleExportCSV}>
                <FaDownload />Exporter CSV
              </button>
            </div>
          </div>

          {/* ── STATS ── */}
          <div className="nl-stats">
            {[
              { label:'Total Abonnés',    num:stats.total,    tag:'TOTAL',    accent:'#8fa3b5', color:P.title,   icon:FaEnvelope,    strip:'linear-gradient(180deg,#fd7e14,#dc3545)' },
              { label:'Abonnés Actifs',   num:stats.active,   tag:'ACTIFS',   accent:'#198754', color:'#198754', icon:FaCheckCircle, strip:'linear-gradient(180deg,#198754,#20c997)' },
              { label:'Abonnés Inactifs', num:stats.inactive, tag:'INACTIFS', accent:'#dc3545', color:'#dc3545', icon:FaTimesCircle, strip:'linear-gradient(180deg,#dc3545,#fd7e14)' },
              { label:'Croissance',       num:stats.growth,   tag:'CE MOIS',  accent:'#fd7e14', color:'#fd7e14', icon:FaChartLine,   strip:'linear-gradient(180deg,#fd7e14,#20c997)' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="nl-stat" style={{ background:P.card, borderColor:P.cardBorder, boxShadow:P.shadow }}>
                  <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, borderRadius:'14px 0 0 14px', background:s.strip }} />
                  <div className="nl-stat-icon" style={{ background:`${s.accent}12`, color:s.accent, borderColor:`${s.accent}22` }}>
                    {loadingStats ? <div className="nl-spinner" style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${s.accent}`, borderTopColor:'transparent' }} /> : <Icon />}
                  </div>
                  <div className="nl-stat-content">
                    <div className="nl-stat-num" style={{ color:s.color }}>{loadingStats ? '…' : s.num}</div>
                    <div className="nl-stat-label" style={{ color:P.textDim }}>{s.label}</div>
                    <span className="nl-stat-tag" style={{ color:s.accent, background:`${s.accent}10`, borderColor:`${s.accent}20` }}>{s.tag}</span>
                  </div>
                  <div className="nl-stat-deco" style={{ background:s.accent }} />
                </div>
              );
            })}
          </div>

          {/* ── PANEL TABLE ── */}
          <div className="nl-panel" style={{ background:P.panel, borderColor:P.panelBorder, boxShadow:P.shadow }}>

            <div className="nl-panel-head" style={{ borderBottomColor:P.panelBorder }}>
              <div>
                <div className="nl-panel-title" style={{ color:P.title }}>Liste des Abonnés</div>
                <div className="nl-panel-sub"   style={{ color:P.textDim }}>{totalItems} abonné(s) trouvé(s)</div>
              </div>
            </div>

            <div className="nl-filters" style={{ borderBottomColor:P.panelBorder }}>
              <div className="nl-search-wrap">
                <FaSearch className="nl-search-ico" style={{ color:P.textDim }} />
                <input type="text" className="nl-search-input" placeholder="Rechercher par email…"
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ background:P.inputBg, borderColor:P.inputBorder, color:P.title }}
                />
              </div>
              <div className="nl-filter-pills">
                {[
                  { id:'all',      label:'Tous',     Icon:FaFilter,      activeClass:'nl-pill--on'       },
                  { id:'active',   label:'Actifs',   Icon:FaCheckCircle, activeClass:'nl-pill--on-green' },
                  { id:'inactive', label:'Inactifs', Icon:FaTimesCircle, activeClass:'nl-pill--on-red'   },
                ].map(f => (
                  <button key={f.id}
                    className={`nl-pill${filterStatus===f.id?' '+f.activeClass:''}`}
                    onClick={() => setFilterStatus(f.id)}
                    style={{ borderColor:filterStatus===f.id?'transparent':P.filterBorder, color:filterStatus===f.id?undefined:P.text }}
                  >
                    <f.Icon style={{ fontSize:12 }} />{f.label}
                  </button>
                ))}
              </div>
              <span className="nl-result-pill">{totalItems} résultat{totalItems !== 1 ? 's' : ''}</span>
            </div>

            <div className="nl-table-wrap">
              {loading ? (
                <div style={{ padding:24, display:'flex', flexDirection:'column', gap:10 }}>
                  {[...Array(6)].map((_,i) => <Sk key={i} h={56} />)}
                </div>
              ) : subscribers.length > 0 ? (
                <>
                  <table className="nl-table">
                    <thead>
                      <tr style={{ borderBottomColor:P.tableLine }}>
                        {['#','Email',"Date d'inscription",'Source','Statut','Actions'].map((h,i) => (
                          <th key={i} style={{ color:P.textDim, background:P.tableHead, textAlign:i===5?'center':'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub, idx) => (
                        <tr key={sub.id}
                          onClick={() => { setSelectedSubscriber(sub); setShowModal(true); }}
                          style={{ borderBottomColor:P.tableLine }}
                          onMouseEnter={e => e.currentTarget.style.background=P.rowHover}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}
                        >
                          <td onClick={e=>e.stopPropagation()}>
                            <span className="nl-num" style={{ background:P.numBg, color:P.numColor }}>{startIndex+idx+1}</span>
                          </td>
                          <td>
                            <div className="nl-email-cell">
                              <div className="nl-email-av">{sub.email.charAt(0).toUpperCase()}</div>
                              <span className="nl-email-val" style={{ color:P.title }}>{sub.email}</span>
                            </div>
                          </td>
                          <td>
                            <div className="nl-date">
                              <span className="nl-date-main" style={{ color:P.text }}>{formatDate(sub.created_at)}</span>
                              <span className="nl-date-sub"  style={{ color:P.textDim }}>{formatTime(sub.created_at)}</span>
                            </div>
                          </td>
                          <td>
                            <span className="nl-src" style={{ background:P.surface, color:P.text, borderColor:P.surfBorder }}>
                              {sub.source || '—'}
                            </span>
                          </td>
                          <td>
                            <span className={`nl-status ${sub.status}`}>
                              {sub.status==='active' ? <FaCheckCircle /> : <FaTimesCircle />}
                              {sub.status==='active' ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td onClick={e=>e.stopPropagation()}>
                            <div className="nl-acts">
                              <button className="nl-act view" title="Voir"
                                onClick={() => { setSelectedSubscriber(sub); setShowModal(true); }}
                                style={{ borderColor:P.panelBorder, color:P.textDim }}>
                                <FaEye />
                              </button>
                              <button className="nl-act tog" title="Changer statut"
                                disabled={actionLoading===sub.id+'-toggle'}
                                onClick={() => handleToggleStatus(sub.id)}
                                style={{ borderColor:P.panelBorder, color:P.textDim }}>
                                {actionLoading===sub.id+'-toggle'
                                  ? <div className="nl-spinner" style={{ width:14,height:14,borderRadius:'50%',border:'2px solid #198754',borderTopColor:'transparent' }} />
                                  : sub.status==='active' ? <FaToggleOn /> : <FaToggleOff />}
                              </button>
                              <button className="nl-act del" title="Supprimer"
                                disabled={actionLoading===sub.id+'-delete'}
                                onClick={() => handleDelete(sub.id)}
                                style={{ borderColor:P.panelBorder, color:P.textDim }}>
                                {actionLoading===sub.id+'-delete'
                                  ? <div className="nl-spinner" style={{ width:14,height:14,borderRadius:'50%',border:'2px solid #dc3545',borderTopColor:'transparent' }} />
                                  : <FaTrash />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {totalPages > 1 && (
                    <div className="nl-pager" style={{ borderTopColor:P.panelBorder }}>
                      <button className="nl-pg" onClick={() => setCurrentPage(1)}                                  disabled={currentPage===1}          style={{ borderColor:P.panelBorder, color:P.textDim }}>«</button>
                      <button className="nl-pg" onClick={() => setCurrentPage(p => Math.max(p-1,1))}              disabled={currentPage===1}          style={{ borderColor:P.panelBorder, color:P.textDim }}>Précédent</button>
                      <div className="nl-pg-nums">
                        {[...Array(totalPages)].map((_,i) => (
                          <button key={i} className={`nl-pg${currentPage===i+1?' on':''}`}
                            onClick={() => setCurrentPage(i+1)}
                            style={{ borderColor:currentPage===i+1?'#fd7e14':P.panelBorder, color:currentPage===i+1?'#fff':P.textDim }}>
                            {i+1}
                          </button>
                        ))}
                      </div>
                      <button className="nl-pg" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))}     disabled={currentPage===totalPages} style={{ borderColor:P.panelBorder, color:P.textDim }}>Suivant</button>
                      <button className="nl-pg" onClick={() => setCurrentPage(totalPages)}                         disabled={currentPage===totalPages} style={{ borderColor:P.panelBorder, color:P.textDim }}>»</button>
                      <span className="nl-pg-info" style={{ color:P.textDim }}>Page {currentPage}/{totalPages} · {totalItems} abonné{totalItems!==1?'s':''}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="nl-empty">
                  <div className="nl-empty-ico"><FaEnvelope /></div>
                  <h3 style={{ color:P.title }}>Aucun abonné trouvé</h3>
                  <p  style={{ color:P.textDim }}>Essayez de modifier vos critères de recherche</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ── MODAL ── */}
      {showModal && selectedSubscriber && (
        <div className="nl-overlay" onClick={() => setShowModal(false)}>
          <div className="nl-modal" style={{ background:P.modalBg, borderColor:P.modalBorder }} onClick={e=>e.stopPropagation()}>

            <div className="nl-modal-head" style={{ borderBottomColor:P.modalBorder }}>
              <div className="nl-modal-head-l">
                <div className="nl-modal-av">{selectedSubscriber.email.charAt(0).toUpperCase()}</div>
                <div>
                  <div className="nl-modal-h" style={{ color:P.title }}>Détails de l'Abonné</div>
                  <div className="nl-modal-s" style={{ color:P.textDim }}>{selectedSubscriber.email}</div>
                </div>
              </div>
              <button className="nl-modal-x" onClick={() => setShowModal(false)} style={{ borderColor:P.modalBorder, color:P.textDim }}><FaTimes /></button>
            </div>

            <div className="nl-modal-body">
              <div className="nl-detail-sect-title">Informations de l'abonné</div>
              <div className="nl-detail-grid">
                {[
                  { label:'Email',              val:selectedSubscriber.email,                                                             icon:null          },
                  { label:"Date d'inscription", val:`${formatDate(selectedSubscriber.created_at)} à ${formatTime(selectedSubscriber.created_at)}`, icon:FaCalendarAlt },
                  { label:'Source',             val:selectedSubscriber.source || '—',                                                    icon:null          },
                  { label:'Désabonné le',       val:selectedSubscriber.unsubscribed_at ? formatDate(selectedSubscriber.unsubscribed_at) : 'Toujours abonné', icon:FaClock },
                ].map((d,i) => (
                  <div key={i} className="nl-detail-item" style={{ background:P.detailBg, borderColor:P.detailBorder }}>
                    <span className="nl-detail-lbl">{d.label}</span>
                    <span className="nl-detail-val" style={{ color:P.title }}>
                      {d.icon && <d.icon style={{ marginRight:8, fontSize:14, color:'#fd7e14' }} />}
                      {d.val}
                    </span>
                  </div>
                ))}
                <div className="nl-detail-item" style={{ background:P.detailBg, borderColor:P.detailBorder }}>
                  <span className="nl-detail-lbl">Statut actuel</span>
                  <span className={`nl-status ${selectedSubscriber.status}`} style={{ marginTop:4, alignSelf:'flex-start' }}>
                    {selectedSubscriber.status==='active' ? <><FaCheckCircle /><span>Actif</span></> : <><FaTimesCircle /><span>Inactif</span></>}
                  </span>
                </div>
              </div>
            </div>

            <div className="nl-modal-foot" style={{ borderTopColor:P.modalBorder }}>
              <button className="nl-mbt sec"
                disabled={!!actionLoading}
                onClick={() => handleToggleStatus(selectedSubscriber.id)}>
                {actionLoading===selectedSubscriber.id+'-toggle'
                  ? <><div className="nl-spinner" style={{ width:16,height:16,borderRadius:'50%',border:'2px solid #fff',borderTopColor:'transparent' }} /><span>Chargement…</span></>
                  : selectedSubscriber.status==='active'
                    ? <><FaToggleOff /><span>Désactiver</span></>
                    : <><FaToggleOn  /><span>Activer</span></>}
              </button>
              <button className="nl-mbt dng"
                disabled={!!actionLoading}
                onClick={() => handleDelete(selectedSubscriber.id)}>
                {actionLoading===selectedSubscriber.id+'-delete'
                  ? <><div className="nl-spinner" style={{ width:16,height:16,borderRadius:'50%',border:'2px solid #dc3545',borderTopColor:'transparent' }} /><span>Suppression…</span></>
                  : <><FaTrash /><span>Supprimer</span></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Newsletter;